import Fuse from 'fuse.js';

import { Injectable } from '@angular/core';

import { ApContentItem } from '@ap/content';
import { MijnConditiesService } from '@ap/mijn-condities';
import { ApThemeService } from '@ap/theme';

import backupSearchContent from './content/backup-search-content.json';
import { SearchMatch, SearchResult } from './mijn-zoeken.model';

import IFuseOptions = Fuse.IFuseOptions;
export type { IFuseOptions };

@Injectable()
export class MijnZoekenService {
    private isBusiness = false;
    private searchContent: any;
    private readonly redirectLinkKeys: string[] = [];
    private theme = 'default';

    private fuse: { search: (pattern: string | Fuse.Expression, options?: Fuse.FuseSearchOptions) => any };

    public constructor(
        private readonly themeService: ApThemeService,
        private readonly mijnConditiesService: MijnConditiesService
    ) {}

    private static setContentKey(linkKey: string): string {
        const parts = linkKey.split('.');
        parts.pop();
        return parts.join('.');
    }

    private static setName(contentKey: string): string {
        const splitters = ['mijn.klant.zelfservice', 'mijn.template', 'mijn.zoeken', 'mijn-products-zzp', 'mijn-products'];

        const splitter = splitters.find((s) => contentKey.startsWith(s));

        if (splitter) {
            return contentKey.split(`${splitter}.`)[1];
        }

        return contentKey;
    }

    public setIsBusiness(isBusiness: boolean): void {
        this.isBusiness = isBusiness;
    }

    public setUpSearch(headlessContent?: { [key: string]: string }): void {
        this.theme = this.themeService.getTheme();
        if (this.fuse && headlessContent == null) {
            return;
        }
        this.setSearchContent(headlessContent);
        const searchContent = [];
        for (const key in this.searchContent) {
            if (key.indexOf('link') > -1 || key.indexOf('routeKey') > -1) {
                this.redirectLinkKeys.push(key);
            } else {
                searchContent.push({ contentKey: key, value: this.searchContent[key] });
            }
        }
        const options = {
            threshold: 0.6,
            ignoreLocation: true,
            includeScore: true,
            keys: ['default', 'value', this.theme],
            shouldSort: true,
            minMatchCharLength: 3
        } as IFuseOptions<any>;
        this.fuse = new Fuse(searchContent, options);
    }

    public getSearchResults(searchTerm: string): SearchResult[] {
        const matches = this.getSearchMatches(searchTerm);
        const filteredMatches = matches.filter((searchMatch) => this.mijnConditiesService.checkContentKeyCondition(searchMatch.contentKey));
        let allResults = this.mapSearchResults(filteredMatches);
        if (allResults.length === 0) {
            return [];
        }

        allResults = allResults.filter((s) => s.title !== '');
        allResults = this.filterUniqueNames(allResults);
        return allResults;
    }

    private setSearchContent(headlessContent?: { [key: string]: string }): void {
        if (headlessContent) {
            this.searchContent = this.mapHeadlessContentItems(headlessContent);
        } else {
            this.searchContent = this.mapApContentItems(backupSearchContent);
        }
    }

    private mapHeadlessContentItems(headlessContent?: { [key: string]: string }): { [key: string]: string } {
        const mappedContent: { [key: string]: string } = {};

        for (const key in headlessContent) {
            const isBusinessKey = key.startsWith('business.');

            if (isBusinessKey && !this.isBusiness) {
                // Skip business keys for non-business users.
                continue;
            }

            const baseKey = isBusinessKey ? key.slice('business.'.length) : key;

            if (!isBusinessKey && !!mappedContent[key]) {
                // Skip non-business values that were already set by business.
                continue;
            }

            mappedContent[baseKey] = headlessContent[key]
                .replace(/&amp;/gm, '&')
                .replace(/&#\d+;/gm, (s) => String.fromCharCode(parseInt(s.match(/\d+/gm)[0], 10)));
        }

        return mappedContent;
    }

    private mapApContentItems(apContent?: ApContentItem): { [key: string]: string } {
        const mappedContent: { [key: string]: string } = {};
        for (const key in apContent) {
            let keyContent = apContent[key][this.theme] ?? apContent[key].default;
            if (this.isBusiness && !!apContent[key]['business']) {
                const businessContent = apContent[key]['business'] as { [theme: string]: string };
                keyContent = businessContent?.[this.theme] ?? businessContent?.default ?? keyContent;
            }
            mappedContent[key] = keyContent as string;
        }
        return mappedContent;
    }

    private getSearchMatches(searchTerm: string): SearchMatch[] {
        const matches = this.fuse.search(searchTerm);
        return matches.map((match) => ({ ...match.item, score: match.score }));
    }

    private mapSearchResults(matches: SearchMatch[]): SearchResult[] {
        const regex = /.*\.link$/g;
        return matches
            .filter((match) => !match.contentKey.match(regex))
            .map((match) => ({ ...match, contentKey: MijnZoekenService.setContentKey(match.contentKey) }))
            .filter((match) =>
                this.redirectLinkKeys.some(
                    (linkKey) => linkKey.match(`${match.contentKey}.link`) || linkKey.match(`${match.contentKey}.routeKey`)
                )
            )
            .map((match) => ({
                title: this.searchContent[`${match.contentKey}.title`],
                subTitle: this.searchContent[`${match.contentKey}.subtitle`],
                actionType:
                    this.searchContent[`${match.contentKey}.link`] || this.searchContent[`${match.contentKey}.routeKey`]
                        ? 'internalLink'
                        : null,
                score: match.score,
                routeKey: this.searchContent[`${match.contentKey}.routeKey`],
                link: this.searchContent[`${match.contentKey}.link`],
                name: MijnZoekenService.setName(match.contentKey)
            }));
    }

    private filterUniqueNames(results: SearchResult[]): SearchResult[] {
        return results.filter((object, index, self) => index === self.findIndex((o) => o.name === object.name));
    }
}
